import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { parse_response_data as parse_api_response_data } from '@/utils/api_response_helpers';
import {
  apply_binding_result,
  build_failure_message,
  build_selected_frameworks_label,
  create_binding_failure,
  create_binding_success,
  get_bindable_frameworks,
  get_binding_title,
  get_failed_selection_ids,
  get_unique_selected_ids,
  is_valid_assessment_id,
  normalize_framework,
  normalize_selected_framework_ids
} from './bind_risk_framework_helpers';
import {
  ASSIGN_ASSESSMENT_TO_ENTITY_URL,
  LIST_RISK_FRAMEWORKS,
  REMOVE_RISK_BINDING_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'BindRiskFramework',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: [Number, String],
      default: null
    },
    entityTypeName: {
      type: String,
      default: ''
    },
    entityConfigId: {
      type: [Number, String],
      default: null
    },
    riskBindings: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    risk_frameworks: [],
    selected_framework_ids: [],
    creating_binding: false,
    assigning_framework_id: null,
    framework_dropdown_open: false,
    frameworks_loading: false,
    removing_binding_id: null
  }),
  computed: {
    available_frameworks_count() {
      return Array.isArray(this.risk_frameworks) ? this.risk_frameworks.length : 0;
    },
    bound_frameworks_count() {
      return this.active_risk_bindings.length;
    },
    selected_frameworks_count() {
      return Array.isArray(this.selected_framework_ids) ? this.selected_framework_ids.length : 0;
    },
    selected_frameworks_label() {
      return build_selected_frameworks_label({
        frameworks_loading: this.frameworks_loading,
        bindable_frameworks: this.bindable_frameworks,
        selected_frameworks_count: this.selected_frameworks_count
      });
    },
    bindable_frameworks() {
      return get_bindable_frameworks(this.risk_frameworks, this.active_risk_bindings);
    },
    active_risk_bindings() {
      return Array.isArray(this.riskBindings)
        ? this.riskBindings.filter((binding) => binding?.is_active !== false)
        : [];
    }
  },
  mounted() {
    this.fetch_risk_frameworks();
    document.addEventListener('click', this.on_document_click, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_document_click, true);
  },
  methods: {
    on_document_click(event) {
      if (!this.framework_dropdown_open) return;

      const root = this.$refs?.frameworkMultiselect;
      if (root && !root.contains(event.target)) {
        this.framework_dropdown_open = false;
      }
    },

    toggle_framework_dropdown() {
      if (this.creating_binding) return;
      this.framework_dropdown_open = !this.framework_dropdown_open;
    },

    normalize_framework(item, index = 0) {
      return normalize_framework(item, index);
    },

    normalize_selected_framework_ids() {
      return normalize_selected_framework_ids(this.selected_framework_ids);
    },

    async fetch_risk_frameworks() {
      this.frameworks_loading = true;
      try {
        const response = await axios.get(
          LIST_RISK_FRAMEWORKS,
          get_request_config()
        );
        const response_data = parse_api_response_data(response);
        const items = Array.isArray(response_data.data) ? response_data.data : [];
        this.risk_frameworks = items.map((item, index) => this.normalize_framework(item, index));
      } catch (error) {
        this.risk_frameworks = [];
        this.show_error(error?.response?.data?.message || 'Failed to load risk frameworks.');
      } finally {
        this.frameworks_loading = false;
      }
    },

    async bind_single_framework(assessment_id) {
      const entity_type_id = Number(this.entityTypeId);
      if (!is_valid_assessment_id(entity_type_id)) {
        return create_binding_failure(assessment_id, 'Missing entity type id.');
      }

      try {
        const response = await axios.post(
          ASSIGN_ASSESSMENT_TO_ENTITY_URL,
          {
            assessment_id,
            entity_type_id
          },
          get_request_config()
        );

        const response_data = parse_api_response_data(response);
        if (response_data?.status && response_data.status !== 'success') {
          return create_binding_failure(
            assessment_id,
            response_data?.message || 'Failed to assign risk framework.'
          );
        }

        return create_binding_success(assessment_id, response_data);
      } catch {
        return create_binding_failure(assessment_id, 'Failed to assign risk framework.');
      }
    },
    async add_framework_bindings() {
      const selected_ids = this.normalize_selected_framework_ids();

      if (!this.has_valid_selection(selected_ids)) {
        this.show_error('Please select at least one risk framework to bind.');
        return;
      }

      if (this.creating_binding) return;

      this.creating_binding = true;

      try {
        const unique_ids = this.get_unique_valid_ids(selected_ids);
        const { successes, failures } = await this.process_framework_bindings(unique_ids);

        this.handle_binding_results(successes, failures);

        await this.fetch_risk_frameworks();

        this.finalize_selection_state(failures);
      } finally {
        this.creating_binding = false;
      }
    },
    has_valid_selection(selected_ids) {
      return Array.isArray(selected_ids) && selected_ids.length > 0;
    },

    get_unique_valid_ids(selected_ids) {
      return get_unique_selected_ids(selected_ids);
    },

    async process_framework_bindings(unique_ids) {
      const successes = [];
      const failures = [];

      for (const raw_id of unique_ids) {
        const assessment_id = Number(raw_id);

        if (!is_valid_assessment_id(assessment_id)) {
          failures.push(create_binding_failure(raw_id, 'Invalid risk framework selected.'));
          continue;
        }

        const result = await this.bind_single_framework(assessment_id);
        apply_binding_result(result, successes, failures);
      }

      const binding_result = {
        successes: successes,
        failures: failures
      };

      return binding_result;
    },

    handle_binding_results(successes, failures) {
      if (successes.length) {
        this.show_success(
          `${successes.length} risk framework${successes.length === 1 ? '' : 's'} assigned successfully.`
        );

        this.$emit('binding-updated', {
          assignedAssessmentIds: successes
        });
      }

      if (failures.length) {
        this.show_error(build_failure_message(failures));
      }
    },

    finalize_selection_state(failures) {
      this.selected_framework_ids = get_failed_selection_ids(failures);

      if (!failures.length) {
        this.framework_dropdown_open = false;
      }
    },

    get_binding_title(binding) {
      return get_binding_title(binding);
    },

    get_binding_phase(binding) {
      return (
        binding?.assessment_phase ||
        binding?.entity_risk_framework_assessment?.risk_framework_assessment?.assessment_phase ||
        'assigned'
      );
    },

    can_remove_risk_binding(binding_id) {
      return is_valid_assessment_id(binding_id) && !this.removing_binding_id;
    },

    async request_remove_risk_binding(binding_id) {
      const response = await axios.post(
        REMOVE_RISK_BINDING_URL,
        { binding_id },
        get_request_config()
      );

      return parse_api_response_data(response);
    },

    handle_remove_risk_binding_response(response_data, binding_id) {
      if (response_data?.status && response_data.status !== 'success') {
        this.show_error(response_data?.message || 'Failed to remove risk binding.');
        return;
      }

      this.show_success('Risk binding removed successfully.');
      this.$emit('binding-updated', { removedBindingId: binding_id });
    },

    async remove_risk_binding(binding) {
      const binding_id = Number(binding?.id);
      if (!this.can_remove_risk_binding(binding_id)) return;

      this.removing_binding_id = binding_id;

      try {
        const response_data = await this.request_remove_risk_binding(binding_id);
        this.handle_remove_risk_binding_response(response_data, binding_id);
      } catch (error) {
        this.show_error(error?.response?.data?.message || 'Failed to remove risk binding.');
      } finally {
        this.removing_binding_id = null;
      }
    },

    go_to_entity_overview() {
      this.$router.push({
        name: 'EntityConfigDetail',
        params: { entityTypeId: this.entityTypeId }
      });
    }
  }
};
